import { useState, useContext } from 'react';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import { autop } from '@wordpress/autop';
import dynamic from 'next/dynamic';

import { getLanguageId } from '@helpers/language';
import PageContext from '@contexts/PageContext';
import { Container, Modal, Viewer360 } from '@components';
import { checkEnabled } from '@components/OptionalFeature';
import PlusIcon from '@assets/icon/plus.svg';
import styles from './ProductDescription.module.scss';

const DynamicImage = dynamic(() => import('@components/DynamicImage'), {
  ssr: false
});

const ProductDescription = ({ data, children, id, DarkMode, componentIsAvailable, showTitle }) => {
  const pageData = useContext(PageContext);
  const [showModal, setShowModal] = useState(false);
  const router = useRouter();
  const { locale } = router;

  const applications = pageData.ProductTags?.filter((item) => item.Tag === 'Application');

  // check 360 Enabled
  const display360 = checkEnabled(data?.DisplayOptions, 'Display360');

  //Display application in overview
  const DisplayApplications = checkEnabled(data?.DisplayOptions, 'DisplayApplications');

  //check Description Available
  const descriptionAvailable = checkEnabled(data?.DisplayOptions, 'Descriptions');

  //check LargeDescription available
  const largeDescriptionAvailable = checkEnabled(data?.DisplayOptions, 'LargeDescription');

  const hasBackground = checkEnabled(data?.DisplayOptions, 'Background');

  const hasBackgroundHero = checkEnabled(data?.DisplayOptions, 'BackgroundHero');

  const overviewDescription = checkEnabled(data?.DisplayOptions, 'OverviewDescription');

  //360 image source
  const imgPath = pageData.ProductGallery.find((x) => x.Description == '360')?.GalleryItems;

  //360 labels
  const labels = imgPath?.map((item) => ({
    id: item.Id.toString(),
    label: item.ProductGalleryContent.Description
  }));

  //check if 360 image is not sirv or spin , add a new class => style needs to have height
  const urlsArray = pageData.ProductGallery.find((x) => x.Description == '360')?.GalleryItems.map((x) => x.ProductGalleryContent.FileNameUrl);
  const specialUrl = urlsArray?.find((x) => !x.includes("sirv") || !x.includes("spin"));

  return (
    componentIsAvailable && (
      //style={ (!display360 && hasBackground) ? backGoundStyle : null}
      //Above line is ready to add to overview <section> in order to show background if there is no 360
      <section className={styles.Section} id={id}>
        <Container className={styles.Container}>
          {display360 && (
            <div className={specialUrl?styles.ViewerWithHeight : styles.Viewer}>
              <Viewer360
                Id="product-description-360"
                DarkMode={DarkMode}
                subMenuOptions={labels}
              />
            </div>
          )}

          {!display360 && (
            <div className={styles.ImageViewer}>
              <DynamicImage
                imgSrc={`/api/static/media/byfile/products/${pageData.ProductCode}/Overview/1_N_N_N_I_720X500.jpg`}
                size={'720x500'}
              />
            </div>
          )}
          {componentIsAvailable && (
            <div className={styles.Content}>
              {showTitle && <h2 className={styles.Title}>{pageData?.ProductText?.Title}</h2>}
              <div className={styles.Buttons}>
                {DisplayApplications && (
                  <>
                    <button
                      className={styles.Button}
                      onClick={() => setShowModal(true)}
                    >
                      <span className={styles.ButtonLabel}>
                        {applications.length} Applications
                      </span>
                      <PlusIcon className={styles.ButtonIcon} />
                    </button>
                    <Modal
                      title="Applications"
                      type="ApplicationsModal"
                      show={showModal}
                      onHide={() => setShowModal(false)}
                    >
                      {descriptionAvailable && (
                        <ul className={styles.ApplicationsList}>
                          {applications.map((application, index) => (
                            <li key={`application-${application.Id}`}>
                              {locale == 'en' ? application.Descriptions[0].EngDesc : application.Descriptions[0].FrDesc}
                            </li>
                          ))}
                        </ul>
                      )}
                    </Modal>
                  </>
                )}
              </div>
              {children}
              {largeDescriptionAvailable && (
                <div
                  dangerouslySetInnerHTML={{
                    __html: autop(pageData.ProductText?.LargeDescription)
                  }}
                />
              )}
              {overviewDescription && pageData.ProductText?.Overview && (
                <div
                  dangerouslySetInnerHTML={{
                    __html: autop(pageData.ProductText?.Overview)
                  }}
                />
              )}
            </div>
          )}
        </Container>
      </section>
    )
  );
};

export default ProductDescription;
